"use client";

import { V6LeftCategorySidebar } from "@/components/v6/LeftCategorySidebar";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Flame,
  Briefcase,
  Waves,
  Coins,
  TrendingUp,
  Settings as SettingsIcon,
  LifeBuoy,
  Sparkles,
  Home as HomeIcon,
  Plus,
  Scale,
  Vote,
  Ticket,
  Users,
  DollarSign,
  Building2,
  ShieldCheck,
  MessageSquare,
  Flag,
  Droplets,
  HeartPulse,
  ScrollText,
  Bot,
  ClipboardCheck,
  Zap,
  Drama,
  Star,
  Bug,
  SlidersHorizontal,
  Hash,
  Globe,
  Landmark,
  Trophy,
  Bitcoin,
  Microscope,
  Film,
  Earth,
  Megaphone,
  ArrowDownToLine,
  ArrowUpFromLine,
  Newspaper,
  FileText,
  ChevronRight,
  ChevronDown,
  LineChart,
  Shield,
  Activity,
  Wrench,
  Rss,
  Layers,
  Gavel,
  CreditCard,
  KeyRound,
  type LucideIcon,
} from "lucide-react";
import { api, loadTokens } from "@/lib/api";
import {
  BADGE_CLASSNAMES,
  isBadgeActive,
  resolveBadgeLabel,
  type FeatureBadge,
} from "@/lib/feature-badges";

// EPIC-030 STORY-1803 — sidebar nav model. Labels come from the
// i18n catalog at render time (t(`sidebar.${tKey}`)); the static
// `label` field stays as the default-language fallback so the
// component still works under tests / SSR before i18n hydrates.
//
// Vercel-redesign 2026-04-30 evening: emoji icons → lucide line
// icons. Founder feedback: "and why the icons looks liek so ugly".
// Every NAV_ITEMS / ADMIN_ITEMS / CATEGORIES row now carries a
// LucideIcon type instead of an emoji string. Render path uses
// <Icon size={14} strokeWidth={1.5} />.
type NavItem = {
  href: string;
  Icon: LucideIcon;
  label: string;
  tKey?: string;
  auth?: boolean;
  match?: (p: string, sp: URLSearchParams) => boolean;
  /**
   * Optional feature badge ("New" / "Live" / "Beta" / "Soon"). When
   * present + still within its expiry, renders a small pill on the
   * right of the row. See apps/web/lib/feature-badges.ts.
   */
  badge?: FeatureBadge;
};

const NAV_ITEMS: NavItem[] = [
  { href: "/markets", Icon: BarChart3, label: "Markets", tKey: "markets" },
  // EPIC-044 — topic-pages subject hubs.
  {
    href: "/topics",
    Icon: Hash,
    label: "Topics",
    tKey: "topics",
    match: (p: string) => p === "/topics" || p.startsWith("/topic/"),
  },
  {
    href: "/markets?sort=trending",
    Icon: Flame,
    label: "Trending",
    tKey: "trending",
    match: (p: string, sp: URLSearchParams) =>
      p === "/markets" && sp.get("sort") === "trending",
  },
  {
    href: "/home",
    Icon: Briefcase,
    label: "Dashboard",
    tKey: "dashboard",
    auth: true,
  },
  // EPIC-006 STORY-604 — personalized feed.
  { href: "/feed", Icon: Waves, label: "Feed", tKey: "feed", auth: true },
  // feat/news-feed-twitter-style-tier3 — Twitter-style news feed.
  // Public surface (no auth gate) so unauthenticated visitors can
  // explore the news-hub output before signing up.
  {
    href: "/feed/news",
    Icon: Newspaper,
    label: "News",
    tKey: "news",
    match: (p: string) => p === "/feed/news" || p.startsWith("/feed/news/"),
  },
  // EPIC-027 STORY-696 — liquidity rewards dashboard.
  {
    href: "/rewards",
    Icon: Coins,
    label: "Rewards",
    tKey: "rewards",
    auth: true,
  },
  {
    href: "/portfolio",
    Icon: TrendingUp,
    label: "Portfolio",
    tKey: "portfolio",
    auth: true,
    match: (p: string) => p.startsWith("/portfolio"),
  },
  // EPIC-029 STORY-1702/1709/1710 — Transak deposit + withdrawal
  // surfaces. Both gated by regions.real_money_enabled in production
  // env; sandbox env unlocks for testing.
  {
    href: "/deposit",
    Icon: ArrowDownToLine,
    label: "Deposit",
    tKey: "deposit",
    auth: true,
    match: (p: string) => p.startsWith("/deposit"),
    // EPIC-029 STORY-1702 — Deposit surface shipped 2026-05-02 AM.
    // Founder direction: 1-day "New" badge so users + founder spot
    // the new feature. Badge auto-expires; bump expiresAt to extend.
    badge: { kind: "new", expiresAt: "2026-05-03T23:59:00Z" },
  },
  {
    href: "/withdraw",
    Icon: ArrowUpFromLine,
    label: "Withdraw",
    tKey: "withdraw",
    auth: true,
    match: (p: string) => p.startsWith("/withdraw"),
    badge: { kind: "new", expiresAt: "2026-05-03T23:59:00Z" },
  },
  {
    href: "/settings",
    Icon: SettingsIcon,
    label: "Settings",
    tKey: "settings",
    auth: true,
    match: (p: string) => p.startsWith("/settings"),
  },
  {
    // STORY-661 — user-facing help-desk surface.
    href: "/help/mine",
    Icon: LifeBuoy,
    label: "Help",
    tKey: "help",
    auth: true,
    match: (p: string) => p.startsWith("/help"),
  },
  {
    // EPIC-005 STORY-509 — PI leaderboard. Subscribers click here
    // first ("who can I copy?"), so it gets the icon weight of the
    // top-level PI nav. /pi/apply still reachable from the empty
    // state on the leaderboard page.
    href: "/pi/leaderboard",
    Icon: Sparkles,
    label: "Popular Investors",
    tKey: "popularInvestors",
    auth: true,
    match: (p: string) => p.startsWith("/pi/"),
  },
  {
    // EPIC-031 STORY-1100 — builder/influencer-GTM signup surface.
    // Authed-only because the application form is the primary
    // surface; the public /builders/[slug] profile page is reachable
    // from a builder's referral URL without needing this nav entry.
    href: "/builders/apply",
    Icon: Megaphone,
    label: "Builders",
    tKey: "builders",
    auth: true,
    match: (p: string) => p.startsWith("/builders"),
    // EPIC-031 STORY-1100/1101 shipped 2026-05-02. New-feature badge.
    badge: { kind: "new", expiresAt: "2026-05-03T23:59:00Z" },
  },
];

// 2026-05-06 — admin sidebar reorganization (PR E). Replaced the flat
// 28-item ADMIN_ITEMS list with a two-section tree:
//   1. Top-level shortcuts (Home, Inbox) render as flat links.
//   2. The remaining 47 admin pages live inside 8 expandable groups
//      (Markets / News Hub / Users / Content & Moderation / Operations
//      / Support / Settings / Dev Tools).
// Open/closed state of each group persists per browser via
// localStorage key `granbay.admin.sidebar`. The group containing the
// current route is force-opened regardless of saved state so the
// active link is always visible.
//
// 14 orphan pages from the audit are promoted into the tree as either
// own entries or sub-page leaves. Settings entries that don't have UI
// yet (Withdrawals/Treasury/FX/Secrets — ship in PR F-H) are
// intentionally omitted; they'll be added in their own PRs to avoid
// dead links.

type AdminLeaf = {
  kind: "leaf";
  href: string;
  Icon: LucideIcon;
  label: string;
  match: (p: string) => boolean;
  /** Optional 3rd-level sub-pages, rendered indented when the parent
   *  leaf or one of the sub-pages is active. */
  subPages?: AdminSubLeaf[];
};

type AdminSubLeaf = {
  href: string;
  label: string;
  match: (p: string) => boolean;
};

type AdminGroup = {
  kind: "group";
  /** Stable key for localStorage persistence + React key. */
  key: string;
  Icon: LucideIcon;
  label: string;
  items: AdminLeaf[];
};

type AdminEntry = AdminLeaf | AdminGroup;

const ADMIN_TREE: AdminEntry[] = [
  // ─── Top-level shortcuts ─────────────────────────────────────
  {
    kind: "leaf",
    href: "/admin",
    Icon: HomeIcon,
    label: "Home",
    match: (p) => p === "/admin",
  },
  {
    kind: "leaf",
    href: "/admin/inbox",
    Icon: ClipboardCheck,
    label: "Inbox",
    match: (p) => p === "/admin/inbox",
  },
  // ─── Markets ─────────────────────────────────────────────────
  {
    kind: "group",
    key: "markets",
    Icon: LineChart,
    label: "Markets",
    items: [
      {
        // 2026-05-12 — unified Market Creation Hub. Surfaces every
        // market-in-flight (cluster + competitor drafts + manual
        // creates) in chronological order. Replaces /admin/drafts as
        // the primary entry point; /admin/drafts stays available for
        // narrow draft-only review.
        kind: "leaf",
        href: "/admin/markets/hub",
        Icon: Layers,
        label: "Creation Hub",
        match: (p) => p.startsWith("/admin/markets/hub"),
      },
      {
        kind: "leaf",
        href: "/admin/markets/new",
        Icon: Plus,
        label: "Create",
        match: (p) => p.startsWith("/admin/markets/new"),
      },
      {
        kind: "leaf",
        href: "/admin/markets/resolve",
        Icon: Scale,
        label: "Resolve directly",
        match: (p) => p.startsWith("/admin/markets/resolve"),
      },
      {
        kind: "leaf",
        href: "/admin/resolution-proposals",
        Icon: Vote,
        label: "Resolution queue",
        match: (p) => p.startsWith("/admin/resolution-proposals"),
      },
      {
        kind: "leaf",
        href: "/admin/disputes",
        Icon: Gavel,
        label: "Disputes",
        match: (p) => p.startsWith("/admin/disputes"),
      },
      {
        kind: "leaf",
        href: "/admin/markets/top",
        Icon: TrendingUp,
        label: "Top markets",
        match: (p) => p.startsWith("/admin/markets/top"),
      },
      {
        kind: "leaf",
        href: "/admin/liquidity",
        Icon: Droplets,
        label: "Liquidity",
        match: (p) => p.startsWith("/admin/liquidity"),
      },
      // 2026-05-08 — moved from News Hub group. The competitor scrape
      // (Polymarket / Kalshi) feeds the AI market-drafting flow, so it
      // belongs alongside Top markets / Liquidity rather than alongside
      // RSS / News-items CMS. Rename: "Competitors" → "Market sources"
      // is industry standard (Polymarket calls them "External markets",
      // Kalshi calls them "Reference markets" — "Market sources" reads
      // cleaner and surfaces the provenance without naming a competitor
      // in the sidebar).
      {
        // 2026-05-08 — focused market-only AI drafts queue. Cross-type
        // queue at /admin/drafts mixed posts + translations in with
        // markets, which made publishing markets harder than necessary.
        kind: "leaf",
        href: "/admin/markets/ai-drafts",
        Icon: Sparkles,
        label: "AI drafts",
        match: (p) => p.startsWith("/admin/markets/ai-drafts"),
      },
      {
        kind: "leaf",
        href: "/admin/competitors",
        Icon: TrendingUp,
        label: "Market sources",
        match: (p) => p.startsWith("/admin/competitors"),
      },
    ],
  },
  // ─── News Hub ────────────────────────────────────────────────
  {
    kind: "group",
    key: "news-hub",
    Icon: Newspaper,
    label: "News Hub",
    items: [
      {
        kind: "leaf",
        href: "/admin/news",
        Icon: Layers,
        label: "Clusters",
        match: (p) =>
          p === "/admin/news" ||
          (p.startsWith("/admin/news/") &&
            !p.startsWith("/admin/news/settings")),
      },
      {
        kind: "leaf",
        href: "/admin/drafts",
        Icon: FileText,
        label: "AI drafts",
        match: (p) => p.startsWith("/admin/drafts"),
      },
      {
        kind: "leaf",
        href: "/admin/news-bots",
        Icon: Rss,
        label: "RSS Sources",
        match: (p) => p.startsWith("/admin/news-bots"),
      },
      {
        kind: "leaf",
        href: "/admin/news-items",
        Icon: Newspaper,
        label: "News Items CMS",
        match: (p) => p.startsWith("/admin/news-items"),
      },
      // News Hub Settings page lands in PR G — sidebar entry added
      // there. Omitting now to avoid a dead link.
    ],
  },
  // ─── Users ───────────────────────────────────────────────────
  {
    kind: "group",
    key: "users",
    Icon: Users,
    label: "Users",
    items: [
      {
        kind: "leaf",
        href: "/admin/users",
        Icon: Users,
        label: "Users list",
        match: (p) =>
          p.startsWith("/admin/users") && !p.startsWith("/admin/users/top"),
      },
      {
        kind: "leaf",
        href: "/admin/users/top",
        Icon: Trophy,
        label: "Top users",
        match: (p) => p.startsWith("/admin/users/top"),
      },
      {
        kind: "leaf",
        href: "/admin/invites",
        Icon: Ticket,
        label: "Invites",
        match: (p) => p.startsWith("/admin/invites"),
      },
      {
        kind: "leaf",
        href: "/admin/builders",
        Icon: Megaphone,
        label: "Builders",
        match: (p) => p.startsWith("/admin/builders"),
      },
      {
        kind: "leaf",
        href: "/admin/pi-applications",
        Icon: Star,
        label: "PI applications",
        match: (p) => p.startsWith("/admin/pi-applications"),
      },
      {
        kind: "leaf",
        href: "/admin/kyc/flagged",
        Icon: ShieldCheck,
        label: "KYC review",
        match: (p) => p.startsWith("/admin/kyc"),
      },
    ],
  },
  // ─── Content & Moderation ────────────────────────────────────
  {
    kind: "group",
    key: "content",
    Icon: Shield,
    label: "Content & Moderation",
    items: [
      {
        kind: "leaf",
        href: "/admin/comments",
        Icon: MessageSquare,
        label: "Comments",
        match: (p) => p.startsWith("/admin/comments"),
      },
      {
        kind: "leaf",
        href: "/admin/moderation",
        Icon: Flag,
        label: "Moderation",
        match: (p) => p.startsWith("/admin/moderation"),
      },
      {
        kind: "leaf",
        href: "/admin/posts/top",
        Icon: TrendingUp,
        label: "Top posts",
        match: (p) => p.startsWith("/admin/posts/top"),
      },
      {
        kind: "leaf",
        href: "/admin/communications",
        Icon: Megaphone,
        label: "Communications",
        match: (p) => p.startsWith("/admin/communications"),
      },
    ],
  },
  // ─── Operations ──────────────────────────────────────────────
  {
    kind: "group",
    key: "operations",
    Icon: Activity,
    label: "Operations",
    items: [
      {
        kind: "leaf",
        href: "/admin/withdrawals",
        Icon: ArrowUpFromLine,
        label: "Withdrawals",
        match: (p) => p.startsWith("/admin/withdrawals"),
      },
      {
        kind: "leaf",
        href: "/admin/treasury",
        Icon: Building2,
        label: "Treasury",
        match: (p) => p.startsWith("/admin/treasury"),
        subPages: [
          {
            href: "/admin/treasury/conversion-revenue",
            label: "Conversion revenue",
            match: (p) => p.startsWith("/admin/treasury/conversion-revenue"),
          },
        ],
      },
      {
        kind: "leaf",
        href: "/admin/fees",
        Icon: DollarSign,
        label: "Fee revenue",
        match: (p) => p.startsWith("/admin/fees"),
      },
      {
        kind: "leaf",
        href: "/admin/integrity",
        Icon: ShieldCheck,
        label: "Integrity",
        match: (p) => p.startsWith("/admin/integrity"),
      },
      {
        kind: "leaf",
        href: "/admin/system-health",
        Icon: Activity,
        label: "System health",
        match: (p) => p.startsWith("/admin/system-health"),
      },
      {
        kind: "leaf",
        href: "/admin/copy-health",
        Icon: HeartPulse,
        label: "Copy health",
        match: (p) => p.startsWith("/admin/copy-health"),
      },
      {
        kind: "leaf",
        href: "/admin/metrics",
        Icon: BarChart3,
        label: "Metrics",
        match: (p) => p.startsWith("/admin/metrics"),
      },
      {
        kind: "leaf",
        href: "/admin/audit-events",
        Icon: ScrollText,
        label: "Audit log",
        match: (p) => p.startsWith("/admin/audit-events"),
      },
    ],
  },
  // ─── Support ─────────────────────────────────────────────────
  {
    kind: "group",
    key: "support",
    Icon: LifeBuoy,
    label: "Support",
    items: [
      {
        kind: "leaf",
        href: "/admin/tickets",
        Icon: Ticket,
        label: "Tickets",
        match: (p) =>
          p.startsWith("/admin/tickets") &&
          !p.startsWith("/admin/tickets/templates"),
        subPages: [
          {
            href: "/admin/tickets/templates",
            label: "Templates",
            match: (p) => p.startsWith("/admin/tickets/templates"),
          },
        ],
      },
      {
        kind: "leaf",
        href: "/admin/bot-reports",
        Icon: Bug,
        label: "Bot reports",
        match: (p) =>
          p.startsWith("/admin/bot-reports") &&
          !p.startsWith("/admin/bot-reports/patterns"),
        subPages: [
          {
            href: "/admin/bot-reports/patterns",
            label: "Patterns",
            match: (p) => p.startsWith("/admin/bot-reports/patterns"),
          },
        ],
      },
    ],
  },
  // ─── Settings ────────────────────────────────────────────────
  // Withdrawals Config / Treasury Config / FX Config / Secrets
  // Management ship in PR F-H — entries added in those PRs.
  {
    kind: "group",
    key: "settings",
    Icon: SettingsIcon,
    label: "Settings",
    items: [
      {
        kind: "leaf",
        href: "/admin/regions",
        Icon: Globe,
        label: "Regions & Compliance",
        match: (p) => p.startsWith("/admin/regions"),
      },
      {
        // PR F (2026-05-06) — encrypted runtime secrets management
        // (API keys + provider tokens). Backend ships in this same
        // PR so the entry isn't a dead link.
        kind: "leaf",
        href: "/admin/settings/secrets",
        Icon: KeyRound,
        label: "Secrets Management",
        match: (p) => p.startsWith("/admin/settings/secrets"),
      },
      {
        // 2026-05-08 — visible toggle for the active AI provider (OpenAI
        // vs Anthropic). Replaces the free-text platform_config edit
        // path; backend still reads the same `ai.provider` row.
        kind: "leaf",
        href: "/admin/ai-providers",
        Icon: Sparkles,
        label: "AI providers",
        match: (p) => p.startsWith("/admin/ai-providers"),
      },
      {
        kind: "leaf",
        href: "/admin/config",
        Icon: SlidersHorizontal,
        label: "Platform Config (legacy)",
        match: (p) => p.startsWith("/admin/config"),
      },
    ],
  },
  // ─── Dev Tools ───────────────────────────────────────────────
  {
    kind: "group",
    key: "dev-tools",
    Icon: Wrench,
    label: "Dev Tools",
    items: [
      {
        kind: "leaf",
        href: "/admin/test-bot",
        Icon: Bot,
        label: "Test bot",
        match: (p) => p.startsWith("/admin/test-bot"),
      },
      {
        kind: "leaf",
        href: "/admin/perf-testing",
        Icon: Zap,
        label: "Perf testing",
        match: (p) => p.startsWith("/admin/perf-testing"),
      },
      {
        kind: "leaf",
        href: "/admin/qa-bot",
        Icon: Drama,
        label: "QA personas",
        match: (p) =>
          p === "/admin/qa-bot" ||
          (p.startsWith("/admin/qa-bot/") &&
            !p.startsWith("/admin/qa-bot/activity") &&
            !p.startsWith("/admin/qa-bot/runs")),
        subPages: [
          {
            href: "/admin/qa-bot/activity",
            label: "Activity",
            match: (p) =>
              p.startsWith("/admin/qa-bot/activity") &&
              !p.startsWith("/admin/qa-bot/activity-feed"),
          },
          {
            href: "/admin/qa-bot/activity-feed",
            label: "Activity feed",
            match: (p) => p.startsWith("/admin/qa-bot/activity-feed"),
          },
          {
            href: "/admin/qa-bot/runs",
            label: "Runs",
            match: (p) => p.startsWith("/admin/qa-bot/runs"),
          },
        ],
      },
      {
        kind: "leaf",
        href: "/admin/asaas-preview",
        Icon: CreditCard,
        label: "Asaas preview",
        match: (p) => p.startsWith("/admin/asaas-preview"),
      },
    ],
  },
];

// Determines whether a group's route tree contains the current path.
// Used to force-open the active group regardless of saved state.
function groupContainsActive(group: AdminGroup, pathname: string): boolean {
  return group.items.some(
    (leaf) =>
      leaf.match(pathname) ||
      (leaf.subPages?.some((s) => s.match(pathname)) ?? false),
  );
}

// LocalStorage hook — open/closed state per group, persists per
// browser. Returns a Set of group keys that the user has explicitly
// opened. Active group is force-opened by the renderer separately.
//
// Uses useSyncExternalStore so the read+write loop satisfies React 18's
// concurrent-mode contract and SSR markup matches client first paint
// (the server snapshot is the empty string → empty Set; client mounts
// and re-syncs from localStorage).
const SIDEBAR_STORAGE_KEY = "granbay.admin.sidebar";
const STORAGE_EVENT = "granbay-admin-sidebar-changed";

function readSnapshotRaw(): string {
  if (typeof window === "undefined") return "";
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function getServerSnapshot(): string {
  return "";
}

function subscribeToSidebarStorage(callback: () => void): () => void {
  // Listen for both cross-tab `storage` events and our same-tab
  // synthetic event (localStorage.setItem doesn't fire `storage` in
  // the writing tab).
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === SIDEBAR_STORAGE_KEY) callback();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(STORAGE_EVENT, callback);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(STORAGE_EVENT, callback);
  };
}

function parseSnapshot(raw: string): Set<string> {
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    const next = new Set<string>();
    for (const [k, v] of Object.entries(parsed)) {
      if (v) next.add(k);
    }
    return next;
  } catch {
    return new Set();
  }
}

function useAdminGroupState(): {
  openGroups: Set<string>;
  toggle: (key: string) => void;
} {
  const raw = useSyncExternalStore(
    subscribeToSidebarStorage,
    readSnapshotRaw,
    getServerSnapshot,
  );
  const openGroups = useMemo(() => parseSnapshot(raw), [raw]);
  const toggle = useCallback((key: string) => {
    const current = parseSnapshot(readSnapshotRaw());
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    try {
      const obj: Record<string, boolean> = {};
      for (const k of next) obj[k] = true;
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(obj));
      // Same-tab notification — `storage` event only fires across
      // tabs, so we dispatch a synthetic event for in-tab subscribers.
      window.dispatchEvent(new Event(STORAGE_EVENT));
    } catch {
      // ignore — read-only or quota-full localStorage shouldn't break
      // the toggle; user just won't get persistence this session.
    }
  }, []);
  return { openGroups, toggle };
}

type CategoryItem = {
  slug: string;
  Icon: LucideIcon;
  label: string;
  /** i18n key under `sidebar.category.<key>` namespace. */
  tKey: string;
};

const CATEGORIES: CategoryItem[] = [
  { slug: "all", Icon: Globe, label: "All categories", tKey: "all" },
  { slug: "politics", Icon: Landmark, label: "Politics", tKey: "politics" },
  { slug: "sports", Icon: Trophy, label: "Sports", tKey: "sports" },
  { slug: "crypto", Icon: Bitcoin, label: "Crypto", tKey: "crypto" },
  { slug: "economics", Icon: TrendingUp, label: "Economics", tKey: "economics" },
  { slug: "tech", Icon: Microscope, label: "Tech & Science", tKey: "tech" },
  { slug: "culture", Icon: Film, label: "Culture", tKey: "culture" },
  { slug: "world", Icon: Earth, label: "World", tKey: "world" },
];

// Next.js 16 + Turbopack requires `useSearchParams()` callers to live
// inside a Suspense boundary so the prerender can bail out to a CSR
// fallback while the params resolve. The fallback MUST NOT itself
// call useSearchParams — otherwise the suspend loop trips the build
// prerender ("useSearchParams() should be wrapped in a suspense
// boundary" error on `/admin/asaas-preview`, etc., 2026-05-24).
// V6LeftCategorySidebar reads search params for active-category
// highlight, so the fallback is a hook-free skeleton with the same
// width/height envelope to keep layout stable during hydration.
export function SiteSidebar() {
  // 2026-05-24 — v6 promoted to localhost main theme. Founder ask:
  // every page that renders SiteSidebar should show the v6 BROWSE +
  // NAVIGATION pattern (clean two-section nav, pill active state,
  // mono Lucide icons), not the heavier v1 list with Dashboard /
  // Rewards / Portfolio / Deposit / Withdraw / Admin sub-sections.
  // Delegating here covers all 100+ consumers without forking each
  // page. The v1 implementation (SiteSidebarInner + SiteSidebarFallback)
  // is preserved below in case we need to fall back per route.
  return (
    <Suspense fallback={<SidebarSkeleton />}>
      <V6LeftCategorySidebar />
    </Suspense>
  );
}

// Hook-free placeholder for the SiteSidebar Suspense boundary. Same
// hidden-on-mobile / sticky-on-md layout as V6LeftCategorySidebar so
// the page doesn't shift when the real sidebar hydrates. Renders no
// nav rows — the real component owns the active-state highlighting
// and that's gated on useSearchParams.
function SidebarSkeleton() {
  return (
    <aside
      aria-hidden="true"
      className="hidden flex-col gap-1 pt-4 md:sticky md:top-4 md:flex md:max-h-[calc(100vh-2rem)] md:overflow-y-auto"
    />
  );
}

// Shared auth+admin detection used by SiteSidebar (desktop) and
// MobileSidebarDrawer (phone hamburger). One source of truth for which
// nav items render to which user.
export function useSidebarAuthState() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const compute = () => {
      const tokens = loadTokens();
      if (!cancelled) setIsLoggedIn(!!tokens);
      if (!tokens) {
        if (!cancelled) setIsAdmin(false);
        return;
      }
      void api
        .me()
        .then((profile) => {
          if (!cancelled) setIsAdmin(profile.role === "admin");
        })
        .catch(() => {
          if (!cancelled) setIsAdmin(false);
        });
    };
    compute();
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === "granbay.tokens") compute();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return { isAdmin, isLoggedIn };
}

// Shared sidebar body — the 3 sections (Navigation / Admin / Categories)
// rendered identically in the desktop sticky aside and the mobile
// off-canvas drawer. Callers wrap in their own chrome (aside vs drawer
// dialog).
export function SidebarBody() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const { isAdmin, isLoggedIn } = useSidebarAuthState();
  const activeCategory =
    searchParams.get("category") ?? (pathname === "/markets" ? "all" : null);

  function buildCategoryHref(slug: string): string {
    if (slug === "all") return "/markets";
    const params = new URLSearchParams(searchParams.toString());
    params.set("category", slug);
    params.delete("cursor");
    return `/markets?${params.toString()}`;
  }

  return (
    <>
      <div className="py-2">
        <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
          {t("sidebar.navigation", "Navigation")}
        </div>
        {NAV_ITEMS.filter((item) => !item.auth || isLoggedIn).map((item) => {
          const isActive = item.match
            ? item.match(pathname, searchParams)
            : pathname === item.href.split("?")[0];
          return (
            <SidebarLink
              key={item.label}
              href={item.href}
              Icon={item.Icon}
              label={t(`sidebar.${item.tKey}`, item.label)}
              active={isActive}
              badge={item.badge}
            />
          );
        })}
      </div>

      {isAdmin && <AdminTreeSection pathname={pathname} />}

      <div className="py-2">
        <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
          {t("sidebar.categories", "Categories")}
        </div>
        {CATEGORIES.map((cat) => {
          const isActive = activeCategory === cat.slug;
          return (
            <SidebarLink
              key={cat.slug}
              href={buildCategoryHref(cat.slug)}
              Icon={cat.Icon}
              label={t(`sidebar.category.${cat.tKey}`, cat.label)}
              active={isActive}
            />
          );
        })}
      </div>
    </>
  );
}

function SiteSidebarInner() {
  return (
    <aside className="sticky top-[72px] hidden max-h-[calc(100vh-88px)] self-start overflow-y-auto md:block">
      <SidebarBody />
    </aside>
  );
}

// ─── Admin tree section ─────────────────────────────────────────
// PR E (2026-05-06) — replaces the flat ADMIN_ITEMS render with the
// grouped ADMIN_TREE. Top-level leaves (Home / Inbox) render at the
// top followed by 8 expandable groups. The group containing the
// active route force-opens regardless of saved state.
function AdminTreeSection({ pathname }: { pathname: string }) {
  const { t } = useTranslation();
  const { openGroups, toggle } = useAdminGroupState();
  return (
    <div className="py-2">
      <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
        {t("sidebar.admin")}
      </div>
      {ADMIN_TREE.map((entry) => {
        if (entry.kind === "leaf") {
          return (
            <AdminLeafLink
              key={entry.label}
              entry={entry}
              pathname={pathname}
              indented={false}
            />
          );
        }
        const containsActive = groupContainsActive(entry, pathname);
        const isOpen = openGroups.has(entry.key) || containsActive;
        return (
          <AdminGroupRow
            key={entry.key}
            entry={entry}
            isOpen={isOpen}
            containsActive={containsActive}
            onToggle={() => toggle(entry.key)}
            pathname={pathname}
          />
        );
      })}
    </div>
  );
}

function AdminGroupRow({
  entry,
  isOpen,
  containsActive,
  onToggle,
  pathname,
}: {
  entry: AdminGroup;
  isOpen: boolean;
  containsActive: boolean;
  onToggle: () => void;
  pathname: string;
}) {
  const { Icon } = entry;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`admin-group-${entry.key}`}
        data-testid={`admin-group-toggle-${entry.key}`}
        className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[13px] transition-colors ${
          containsActive
            ? "text-[var(--color-text)]"
            : "text-[var(--color-text2)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]"
        }`}
      >
        <Icon
          size={14}
          strokeWidth={1.5}
          aria-hidden="true"
          className="flex-shrink-0"
        />
        <span className="flex-1 truncate text-left">{entry.label}</span>
        {isOpen ? (
          <ChevronDown
            size={14}
            strokeWidth={1.5}
            aria-hidden="true"
            className="flex-shrink-0 text-[var(--color-text3)]"
          />
        ) : (
          <ChevronRight
            size={14}
            strokeWidth={1.5}
            aria-hidden="true"
            className="flex-shrink-0 text-[var(--color-text3)]"
          />
        )}
      </button>
      {isOpen && (
        <div
          id={`admin-group-${entry.key}`}
          className="ml-3 border-l border-[var(--color-border)] pl-1"
        >
          {entry.items.map((leaf) => (
            <AdminLeafLink
              key={leaf.href}
              entry={leaf}
              pathname={pathname}
              indented={true}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminLeafLink({
  entry,
  pathname,
  indented,
}: {
  entry: AdminLeaf;
  pathname: string;
  indented: boolean;
}) {
  const isActive = entry.match(pathname);
  return (
    <>
      <SidebarLink
        href={entry.href}
        Icon={entry.Icon}
        label={entry.label}
        active={isActive}
        indent={indented ? 1 : 0}
      />
      {entry.subPages &&
        entry.subPages.map((sub) => {
          const subActive = sub.match(pathname);
          // Only render sub-pages when the parent leaf or one of the
          // sub-pages is active — keeps the sidebar from sprawling
          // with rarely-visited deep links.
          const parentOrSubActive =
            isActive || entry.subPages?.some((s) => s.match(pathname));
          if (!parentOrSubActive) return null;
          return (
            <SidebarSubLink
              key={sub.href}
              href={sub.href}
              label={sub.label}
              active={subActive}
            />
          );
        })}
    </>
  );
}

function SidebarSubLink({
  href,
  label,
  active,
}: {
  href: string;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      href={href}
      className={`relative flex items-center gap-2.5 rounded-md py-[5px] pl-7 pr-2.5 text-[12px] transition-colors ${
        active
          ? "bg-[var(--color-surface)] text-[var(--color-text)]"
          : "text-[var(--color-text3)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text2)]"
      }`}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-[-4px] top-1/4 bottom-1/4 w-[3px] rounded-sm"
          style={{ background: "var(--color-brand)" }}
        />
      )}
      <span className="flex-1 truncate">{label}</span>
    </Link>
  );
}

function SidebarLink({
  href,
  Icon,
  label,
  active,
  badge,
  indent = 0,
}: {
  href: string;
  Icon: LucideIcon;
  label: string;
  active: boolean;
  badge?: FeatureBadge;
  /** 0 = top-level row (default); 1 = indented under an admin group. */
  indent?: 0 | 1;
}) {
  // Vercel-redesign 2026-04-30 — sidebar nav weight history:
  //   600 → 800 (#546) → no override (#552, this PR's predecessor)
  // The bold treatment was removed entirely. Items inherit body
  // weight (450) so light-theme reads clean.
  // Icons are now lucide line-style (was emojis) — see NAV_ITEMS /
  // ADMIN_TREE / CATEGORIES item definitions above.
  const { t } = useTranslation();
  const showBadge = isBadgeActive(badge);
  const indentClass = indent === 1 ? "pl-3 pr-2.5" : "px-2.5";
  return (
    <Link
      href={href}
      className={`relative flex items-center gap-2.5 rounded-md ${indentClass} py-[7px] text-[13px] transition-colors ${
        active
          ? "bg-[var(--color-surface)] text-[var(--color-text)]"
          : "text-[var(--color-text2)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]"
      }`}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-[-4px] top-1/4 bottom-1/4 w-[3px] rounded-sm"
          style={{ background: "var(--color-brand)" }}
        />
      )}
      <Icon
        size={14}
        strokeWidth={1.5}
        aria-hidden="true"
        className="flex-shrink-0"
      />
      <span className="flex-1 truncate">{label}</span>
      {showBadge && badge && (
        <span
          className={`ml-auto rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${BADGE_CLASSNAMES[badge.kind]}`}
          aria-label={resolveBadgeLabel(
            badge.kind,
            t as (k: string, f?: string) => string,
          )}
        >
          {resolveBadgeLabel(
            badge.kind,
            t as (k: string, f?: string) => string,
          )}
        </span>
      )}
    </Link>
  );
}

/**
 * SSR/prerender-safe fallback that doesn't read useSearchParams.
 * Static nav + static categories with no active highlight; the real
 * SiteSidebarInner mounts client-side and replaces this within a
 * frame. Keeping the full visual structure prevents layout shift.
 */
function SiteSidebarFallback() {
  const { t } = useTranslation();
  return (
    <aside className="sticky top-[72px] hidden max-h-[calc(100vh-88px)] self-start overflow-y-auto md:block">
      <div className="py-2">
        <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
          {t("sidebar.navigation", "Navigation")}
        </div>
        {NAV_ITEMS.map((item) => (
          <SidebarLink
            key={item.label}
            href={item.href}
            Icon={item.Icon}
            label={t(`sidebar.${item.tKey}`, item.label)}
            active={false}
            badge={item.badge}
          />
        ))}
      </div>
      <div className="py-2">
        <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
          {t("sidebar.categories", "Categories")}
        </div>
        {CATEGORIES.map((cat) => (
          <SidebarLink
            key={cat.slug}
            href={
              cat.slug === "all" ? "/markets" : `/markets?category=${cat.slug}`
            }
            Icon={cat.Icon}
            label={t(`sidebar.category.${cat.tKey}`, cat.label)}
            active={false}
          />
        ))}
      </div>
    </aside>
  );
}
